import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Flame, Tractor, Layers, ArrowLeft, Phone, Mail, MapPin, Loader2, ChevronRight, Wrench } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const iconMap = {
  Flame: Flame,
  Tractor: Tractor,
  Layers: Layers,
  Wrench: Wrench,
};

const fallbackServices = [
  {
    id: '3',
    slug: 'professionel-montage',
    title: { da: "Montagearbejde" },
    description: { da: "<p>Vi tilbyder komplet montagearbejde af stålkonstruktioner, maskinanlæg, rørledninger og meget mere. Vi rykker ud til både store og små opgaver og sikrer en sikker, præcis og hurtig montering på stedet.</p><p>Med Balsby Montage som din samarbejdspartner er du garanteret et professionelt resultat, der lever op til alle specifikationer og tidsplaner.</p><p>Sikkerhed og kvalitet er vores højeste prioritet under montagearbejdet. Vi koordinerer tæt med dig og eventuelle andre håndværkere for at sikre en gnidningsfri proces fra start til slut.</p>" },
    image: ["https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/d51a1730-b243-4fb1-b541-eb82f9db479c.jpg"],
    icon_name: "Layers",
    order: 1
  },
  {
    id: '1',
    slug: 'professionelt-svejsearbejde',
    title: { da: "Professionelt Svejsearbejde" },
    description: { da: "<p>Vi tilbyder professionelt og certificeret svejsearbejde i højeste kvalitet. Uanset om det drejer sig om rustfrit stål, aluminium eller sort jern, sikrer vi præcise og holdbare samlinger til både industri, byggeri og private projekter.</p><p>Vores ekspertise dækker TIG-, MIG- og MAG-svejsning, og vi går aldrig på kompromis med kvaliteten eller sikkerheden.</p><p>Vi har mange års erfaring med komplekse svejseopgaver og kan løse alt fra mindre reparationer til store, bærende stålkonstruktioner. Alle opgaver udføres med fokus på præcision, holdbarhed og gældende standarder.</p>" },
    image: ["https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/4c8d59aa-f867-43d4-a5a9-779f6b9d3841.jpg"],
    icon_name: "Flame",
    order: 2
  },
  {
    id: '2',
    slug: 'reparation-af-landbrugsmaskiner',
    title: { da: "Reparation af landbrugsmaskiner" },
    description: { da: "<p>Vi tilbyder professionel reparation og vedligeholdelse af alle typer landbrugsmaskiner og traktorer. Med stor erfaring inden for landbrugssektoren sikrer vi, at din maskinpark altid kører optimalt, så du undgår dyre driftsstop i højsæsonen.</p><p>Uanset om det drejer sig om mekanisk arbejde, hydraulik, svejsning af sliddele eller akut fejlfinding, rykker vi hurtigt ud og løser opgaven med høj præcision.</p><p>Vores værksted og servicevogn er fuldt udstyret til at håndtere både planlagt vedligeholdelse og akutte nedbrud direkte i marken eller på din bedrift.</p>" },
    image: ["https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/reparation-af-landbrugsmaskiner.jpg"],
    icon_name: "Tractor",
    order: 3
  }
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
            setAllServices(sorted);
            
            // Find current service by slug
            const current = sorted.find(s => {
              const sSlug = s.slug?.da || s.slug;
              return sSlug === slug;
            });
            setService(current || null);
          } else {
            setAllServices(fallbackServices);
            const current = fallbackServices.find(s => s.slug === slug);
            setService(current || null);
          }
        } else {
          setAllServices(fallbackServices);
          const current = fallbackServices.find(s => s.slug === slug);
          setService(current || null);
        }
      } catch (error) {
        console.error('Error fetching service details:', error);
        setAllServices(fallbackServices);
        const current = fallbackServices.find(s => s.slug === slug);
        setService(current || null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  useEffect(() => {
    if (service) {
      const serviceTitle = service.title?.da || service.title || 'Ydelse';
      document.title = `${serviceTitle} | Balsby Montage ApS`;

      let descriptionText = '';
      if (slug === 'professionel-montage' || service.slug === 'professionel-montage') {
        descriptionText = 'Komplet montagearbejde af stålkonstruktioner, maskinanlæg og rørledninger i høj kvalitet. Kontakt Balsby Montage ApS for professionel montering.';
      } else if (slug === 'professionelt-svejsearbejde' || service.slug === 'professionelt-svejsearbejde') {
        descriptionText = 'Professionelt svejsearbejde i rustfrit stål, aluminium og sort jern. TIG-, MIG- og MAG-svejsning udført med høj præcision af Balsby Montage ApS.';
      } else if (slug === 'reparation-af-landbrugsmaskiner' || service.slug === 'reparation-af-landbrugsmaskiner') {
        descriptionText = 'Professionel reparation og vedligeholdelse af landbrugsmaskiner og traktorer. Hurtig udrykning og solidt smedearbejde fra Balsby Montage ApS i Årre.';
      } else {
        const rawDesc = service.description?.da || service.description || '';
        descriptionText = rawDesc.replace(/<[^>]*>?/gm, '').slice(0, 160);
      }

      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', descriptionText);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', `${serviceTitle} | Balsby Montage ApS`);
      }

      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) {
        ogDesc.setAttribute('content', descriptionText);
      }
    }
  }, [service, slug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          service_interest: service?.title?.da || service?.title || slug,
          form_source: `Underside: ${slug}`
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.'
        });
        setFormData({ name: '', email: '', phone: '', message: '', website_url: '' });
      } else {
        setFormStatus({
          type: 'error',
          message: 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring til os direkte.'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        type: 'error',
        message: 'Der skete en netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#487aad]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center px-4 text-center">
          <h1 className="text-4xl font-medium tracking-wider text-white uppercase mb-4">Ydelsen blev ikke fundet</h1>
          <p className="text-slate-400 mb-8 max-w-md">Vi kunne desværre ikke finde den ydelse, du leder efter. Den kan være blevet flyttet eller slettet.</p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-[#487aad] hover:bg-[#3b6591] text-white font-bold px-6 py-3 transition-all duration-200"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const IconComponent = iconMap[service.icon_name] || Wrench;
  const otherServices = allServices.filter(s => s.id !== service.id);

  return (
    <div className="relative min-h-screen bg-slate-950 text-white antialiased selection:bg-amber-500 selection:text-slate-950">
      {/* Global Background Grid Pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] z-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <Header />

      <main className="relative z-10 pt-24">
        {/* Content Section */}
        <section className="py-16 md:py-24 bg-white text-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              
              {/* Main Text Content */}
              <div className="lg:col-span-8 space-y-8">
                <div className="bg-slate-50 border border-slate-200/60 overflow-hidden shadow-sm">
                  <img 
                    src={service.image?.[0]} 
                    alt={service.title?.da || service.title} 
                    className="w-full h-[300px] md:h-[450px] object-cover"
                  />
                </div>

                <div className="prose prose-lg max-w-none text-slate-700 space-y-6">
                  <h2 className="text-2xl sm:text-3xl font-medium text-black uppercase tracking-wide border-b border-slate-200 pb-3">
                    Om Ydelsen
                  </h2>
                  <div 
                    className="space-y-4 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: service.description?.da || service.description }}
                  />
                </div>

                {/* Why Choose Us for this Service */}
                <div className="bg-slate-50 border border-slate-200/60 p-8 space-y-6">
                  <h3 className="text-xl sm:text-2xl font-medium text-black uppercase tracking-wide">
                    Hvorfor vælge Balsby Montage?
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <h4 className="font-medium text-black text-xl sm:text-2xl uppercase tracking-wide">Høj Faglighed</h4>
                      <p className="text-slate-600 text-sm">Vi er uddannede fagfolk med stor erfaring og de nødvendige certificeringer til at udføre arbejdet korrekt.</p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-black text-xl sm:text-2xl uppercase tracking-wide">Fleksibilitet</h4>
                      <p className="text-slate-600 text-sm">Vi tilpasser os din tidsplan og dine specifikke ønsker, uanset om det er en hasteopgave eller et længerevarende projekt.</p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-black text-xl sm:text-2xl uppercase tracking-wide">Pålidelighed</h4>
                      <p className="text-slate-600 text-sm">En aftale er en aftale. Vi leverer til tiden og til den aftalte pris uden ubehagelige overraskelser.</p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-black text-xl sm:text-2xl uppercase tracking-wide">Lokalt Forankret</h4>
                      <p className="text-slate-600 text-sm">Med base i Årre dækker vi hele lokalområdet og rykker hurtigt ud, når der er brug for os.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar: Contact & Other Services */}
              <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-28">
                
                {/* Quick Contact Box */}
                <div className="bg-[#487aad] text-white p-8 space-y-6 shadow-md">
                  <h3 className="text-2xl font-medium uppercase tracking-wide text-white border-b border-white/20 pb-3">
                    Få et tilbud
                  </h3>
                  <p className="text-white/80 text-sm">
                    Har du en opgave inden for <strong>{service.title?.da || service.title}</strong>? Udfyld formularen, så vender vi hurtigt tilbage med et uforpligtende tilbud.
                  </p>

                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    {/* Honeypot Field */}
                    <div className="sr-only absolute">
                      <label htmlFor="website_url">Do not fill this out if you are human</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleInputChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">Navn *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full bg-white/10 border border-white/20 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-white transition-colors placeholder-white/40"
                        placeholder="Dit fulde navn"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">E-mail *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-white/10 border border-white/20 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-white transition-colors placeholder-white/40"
                        placeholder="din@email.dk"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">Telefon *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-white/10 border border-white/20 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-white transition-colors placeholder-white/40"
                        placeholder="Telefonnummer"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-white/80 mb-1">Besked *</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="4"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full bg-white/10 border border-white/20 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-white transition-colors resize-none placeholder-white/40"
                        placeholder="Beskriv din opgave..."
                      />
                    </div>

                    {formStatus.message && (
                      <div className={`p-3 text-xs font-semibold ${
                        formStatus.type === 'success' ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-900' : 'bg-rose-950/50 text-rose-400 border border-rose-900'
                      }`}>
                        {formStatus.message}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-black hover:bg-zinc-900 text-white font-bold py-3 text-sm uppercase tracking-wider transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sender...
                        </>
                      ) : (
                        'Send forespørgsel'
                      )}
                    </button>
                  </form>

                  {/* Direct contact info */}
                  <div className="pt-4 border-t border-white/20 space-y-3 text-xs text-white/80">
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-white" />
                      <span>Ring direkte: <strong>93 87 23 82</strong></span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-white" />
                      <span>E-mail: <strong>balsbymontage@gmail.com</strong></span>
                    </p>
                  </div>
                </div>

                {/* Other Services List */}
                <div className="bg-slate-50 border border-slate-200/60 p-8 space-y-4">
                  <h3 className="text-xl font-medium uppercase tracking-wide text-black border-b border-slate-200 pb-2">
                    Andre Ydelser
                  </h3>
                  <div className="space-y-2">
                    {otherServices.map((s) => {
                      const sSlug = s.slug?.da || s.slug;
                      return (
                        <Link
                          key={s.id}
                          to={`/ydelse/${sSlug}`}
                          className="flex items-center justify-between p-3 bg-white border border-slate-100 text-slate-700 hover:text-[#487aad] hover:border-[#487aad]/30 transition-all duration-200 shadow-sm hover:shadow-md group"
                        >
                          <span className="text-xs font-bold uppercase tracking-wider">{s.title?.da || s.title}</span>
                          <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      );
                    })}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
